import { COMPUTER_COMMAND_MENU_TRIGGER } from '#pplx/frontend/apps/ask/ask-input/ask-input-types/index.js';
import { useAtMentionUsers } from '#pplx/frontend/apps/ask/mention-typeahead/useAtMentionUsers.js';
import { useMentionTypeaheadContext } from '#pplx/frontend/apps/ask/mention-typeahead/useMentionTypeaheadContext.js';
import { useAtMentionSources } from '#pplx/frontend/apps/ask/sources/hooks/useAtMentionSources.js';
import { useCallback, useMemo, useState } from 'react';
export const useMentionTypeaheadOptions = (params) => {
    const { reason, isLazy = true } = params;
    const [currentTrigger, setCurrentTrigger] = useState('');
    const [mentionSearchQuery, setMentionSearchQuery] = useState(null);
    const mentionTypeaheadContext = useMentionTypeaheadContext(params);
    const { isComputerMode, shouldUseComputerCommandMenu, includeSources, includeUsers, tabOptions: tabs, tabsEnabled: tabAttachmentsEnabled, spacesEnabled: isAtMentionSpacesEnabled, spaceOptions: mentionSpacesOptions, onTriggerSpaces, skillsEnabled: isSkillsTypeaheadEnabled, skillOptions: skillsTypeaheadOptions, onTriggerSkills, searchModesEnabled: isAtMentionSearchModesEnabled, searchModeOptions: mentionSearchModesOptions, devicesEnabled: isAtMentionDevicesEnabled, deviceOptions: mentionDevicesOptions, onTriggerDevices, onMentionSource, } = mentionTypeaheadContext;
    const { isAtMentionUsersEnabled, mentionUsersOptions } = useAtMentionUsers({
        reason,
        searchQuery: currentTrigger === '@' ? (mentionSearchQuery ?? undefined) : undefined,
        enabled: includeUsers,
    });
    const { isAtMentionSourcesEnabled, mentionSourcesOptions, mentionIconRenderer, onTrigger: onTriggerSources, } = useAtMentionSources({
        isLazy,
        enabled: includeSources,
        onMentionSource,
        searchQuery: mentionSearchQuery ?? undefined,
        productSurface: isComputerMode ? 'computer' : 'ask',
    });
    const mentionTypeaheadOptions = useMemo(() => {
        if (currentTrigger === COMPUTER_COMMAND_MENU_TRIGGER && shouldUseComputerCommandMenu) {
            return [];
        }
        return [
            ...(isAtMentionDevicesEnabled && currentTrigger === '@' ? mentionDevicesOptions : []),
            ...(tabAttachmentsEnabled && currentTrigger === '@' ? tabs : []),
            ...(isAtMentionSpacesEnabled && currentTrigger === '@' ? mentionSpacesOptions : []),
            ...(isAtMentionSourcesEnabled && currentTrigger === '@' ? mentionSourcesOptions : []),
            ...(isAtMentionUsersEnabled && currentTrigger === '@' ? mentionUsersOptions : []),
            ...(isAtMentionSearchModesEnabled && currentTrigger === COMPUTER_COMMAND_MENU_TRIGGER
                ? mentionSearchModesOptions
                : []),
            ...(isSkillsTypeaheadEnabled && currentTrigger === COMPUTER_COMMAND_MENU_TRIGGER
                ? skillsTypeaheadOptions
                : []),
        ];
    }, [
        isAtMentionSearchModesEnabled,
        mentionSearchModesOptions,
        tabAttachmentsEnabled,
        currentTrigger,
        tabs,
        isAtMentionUsersEnabled,
        mentionUsersOptions,
        isAtMentionDevicesEnabled,
        mentionDevicesOptions,
        isAtMentionSpacesEnabled,
        mentionSpacesOptions,
        isAtMentionSourcesEnabled,
        mentionSourcesOptions,
        isSkillsTypeaheadEnabled,
        skillsTypeaheadOptions,
        shouldUseComputerCommandMenu,
    ]);
    const onTriggerTypeahead = useCallback((trigger, query) => {
        setCurrentTrigger(trigger);
        if (trigger === '@') {
            if (query !== undefined) {
                setMentionSearchQuery(query);
            }
            onTriggerDevices();
            onTriggerSpaces();
            onTriggerSources();
        }
        else if (trigger === COMPUTER_COMMAND_MENU_TRIGGER) {
            if (shouldUseComputerCommandMenu)
                return;
            onTriggerSkills();
        }
    }, [
        onTriggerDevices,
        onTriggerSkills,
        onTriggerSpaces,
        onTriggerSources,
        shouldUseComputerCommandMenu,
    ]);
    const onMentionSearchQueryChange = useCallback((query) => {
        setMentionSearchQuery(query);
    }, []);
    return useMemo(() => ({
        mentionTypeaheadContext,
        mentionTypeaheadOptions,
        mentionIconRenderer: includeSources ? mentionIconRenderer : undefined,
        onMentionSource,
        onTriggerTypeahead,
        onMentionSearchQueryChange,
    }), [
        includeSources,
        mentionIconRenderer,
        mentionTypeaheadContext,
        mentionTypeaheadOptions,
        onMentionSource,
        onTriggerTypeahead,
        onMentionSearchQueryChange,
    ]);
};
export const useComputerCommandMenuMentionTypeahead = (params) => {
    const [computerCommandMenuOpen, setComputerCommandMenuOpen] = useState(false);
    const mentionTypeahead = useMentionTypeaheadOptions({
        ...params,
        useComputerCommandMenu: true,
        onComputerCommandMenuOpenChange: setComputerCommandMenuOpen,
    });
    return useMemo(() => ({
        ...mentionTypeahead,
        computerCommandMenuOpen,
        onComputerCommandMenuOpenChange: setComputerCommandMenuOpen,
    }), [computerCommandMenuOpen, mentionTypeahead]);
};
//# sourceMappingURL=useMentionTypeaheadOptions.js.map